/*
 * The one list of agent outcomes waiting on me, across every scope. Tabs, the
 * Agents menu, the banners, Home's For review, the page title and the favicon
 * all read it. Fetched from `/api/agents/attention`, refetched on the user
 * stream's `attention.updated` (a signal moved on any thread), patched from
 * `thread.updated`, and polled only while the stream is down.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode
} from "react";

import type { AgentThread } from "@/features/agents/api/agentApi";
import { useGetUserDetails } from "@/hooks/useGetUserDetails";
import { useRealtime, useRealtimeEvent } from "@/lib/realtime/RealtimeProvider";

import { AttentionAPI, SNOOZE_MINUTES } from "../api";
import { useAttentionDocumentSignals } from "../hooks/useAttentionDocumentSignals";
import { useAttentionOsNotifications } from "../hooks/useAttentionOsNotifications";
import { countsTowardBadge, itemScope, itemTabId } from "../items";
import { byUrgency } from "../signalTone";
import type { AttentionItem } from "../types";

export {
  countsTowardBadge,
  isSnoozed,
  itemRoute,
  itemScope,
  itemTabId,
  tabIdForScope
} from "../items";

/** The fallback poll, while the realtime stream is not open. */
export const ATTENTION_POLL_MS = 120_000;
const REFRESH_DEBOUNCE_MS = 400;

/**
 * Apply a `thread.updated` payload. Returns the next list, or null when the
 * thread gained a signal we have no row for (the caller refetches). Realtime
 * payloads have no caller, so whether the signal is mine comes from its
 * `uids`, and my snooze is kept from the row.
 */
export const applyThreadUpdate = (
  items: AttentionItem[],
  thread: Pick<AgentThread, "id"> & { signal?: AgentThread["signal"] },
  uid?: string | null
): AttentionItem[] | null => {
  const idx = items.findIndex((i) => i.thread_id === thread.id);
  const { signal } = thread;
  if (signal === undefined) return items;
  const callerless = Boolean(signal?.uids) && !signal?.for_me;
  const forMe =
    !!signal &&
    (callerless ? !!uid && !!signal.uids?.includes(uid) : signal.for_me);
  if (!signal || !forMe) {
    return idx < 0 ? items : items.filter((i) => i.thread_id !== thread.id);
  }
  if (idx < 0) return null;
  const cur = items[idx];
  const snoozedUntil = callerless ? cur.snoozed_until : signal.snoozed_until;
  if (
    cur.kind === signal.kind &&
    cur.since === signal.since &&
    (cur.issues ?? null) === (signal.issues ?? null) &&
    cur.snoozed_until === snoozedUntil
  )
    return items;
  // A newer signal carries a new summary: refetch it, keep the row meanwhile.
  if (cur.since !== signal.since) return null;
  const next = items.slice();
  next[idx] = {
    ...cur,
    kind: signal.kind,
    issues: signal.issues,
    snoozed_until: snoozedUntil
  };
  return next;
};

export type AgentAttentionValue = {
  /** Most urgent first. */
  items: AttentionItem[];
  /** The badge: items not snoozed, less finishes older than a day. */
  count: number;
  loaded: boolean;
  /** Items per app tab id, most urgent first. */
  byTab: Map<string, AttentionItem[]>;
  refresh: () => Promise<void>;
  /** I've opened it: finished/failed leave my list at once. */
  markSeen: (threadId: string) => void;
  /** Snooze banners and OS alerts for these items; they stay in Agents. Resolves to the snooze end. */
  snooze: (items: AttentionItem[], minutes?: number) => Promise<string | null>;
  /** Take these items off my list until their threads raise something new. Resolves to whether all landed. */
  dismiss: (items: AttentionItem[]) => Promise<boolean>;
  /** Skip a question unanswered: the agent carries on without it. Rejects when the server refused. */
  skip: (item: AttentionItem) => Promise<void>;
};

/** One signal on one thread; a newer signal on the thread is a new key. */
export const signalKey = (
  item: Pick<AttentionItem, "thread_id" | "since">
): string => `${item.thread_id}:${item.since ?? ""}`;

const EMPTY: AgentAttentionValue = {
  items: [],
  count: 0,
  loaded: false,
  byTab: new Map(),
  refresh: async () => undefined,
  markSeen: () => undefined,
  snooze: async () => null,
  dismiss: async () => true,
  skip: async () => undefined
};

const Ctx = createContext<AgentAttentionValue>(EMPTY);

/** Null-safe: outside the provider (tests, public pages) the list is empty. */
export const useAgentAttention = (): AgentAttentionValue => useContext(Ctx);

/** Title, favicon and desktop alerts, outside the app's own surfaces. */
const AttentionEffects: FC<{ items: AttentionItem[]; loaded: boolean }> = ({
  items,
  loaded
}) => {
  useAttentionDocumentSignals(items);
  useAttentionOsNotifications(items, loaded);
  return null;
};

export const AgentAttentionProvider: FC<{
  children: ReactNode;
  pollMs?: number;
}> = ({ children, pollMs = ATTENTION_POLL_MS }) => {
  const [items, setItems] = useState<AttentionItem[]>([]);
  const [loaded, setLoaded] = useState(false);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const { userDetails } = useGetUserDetails();
  const uidRef = useRef(userDetails?.id ?? null);
  uidRef.current = userDetails?.id ?? null;
  const debounce = useRef<number | null>(null);
  // Dismissed or skipped here: kept off a refetch that lands before the server has it.
  const hidden = useRef(new Set<string>());

  const refresh = useCallback(async () => {
    try {
      const next = await AttentionAPI.list();
      setItems(
        byUrgency(next.filter((i) => !hidden.current.has(signalKey(i))))
      );
    } catch {
      // Offline or signed out: keep what we have; the poll retries.
    } finally {
      setLoaded(true);
    }
  }, []);

  const scheduleRefresh = useCallback(() => {
    if (debounce.current) window.clearTimeout(debounce.current);
    debounce.current = window.setTimeout(() => {
      debounce.current = null;
      void refresh();
    }, REFRESH_DEBOUNCE_MS);
  }, [refresh]);

  const { status } = useRealtime();
  const streamOpen = status === "open";

  useEffect(() => {
    void refresh();
    const onVisible = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      if (debounce.current) window.clearTimeout(debounce.current);
    };
  }, [refresh]);

  useEffect(() => {
    if (streamOpen) return undefined;
    const timer = window.setInterval(() => void refresh(), pollMs);
    return () => window.clearInterval(timer);
  }, [pollMs, refresh, streamOpen]);

  useRealtimeEvent("attention.updated", () => scheduleRefresh());

  useRealtimeEvent<AgentThread>("thread.updated", (thread) => {
    if (!thread?.id) return;
    const next = applyThreadUpdate(itemsRef.current, thread, uidRef.current);
    if (next === null) scheduleRefresh();
    else if (next !== itemsRef.current) setItems(byUrgency(next));
  });
  useRealtimeEvent<{ id?: string; thread_id?: string }>(
    "thread.deleted",
    (data) => {
      const id = data?.id ?? data?.thread_id;
      if (id && itemsRef.current.some((i) => i.thread_id === id))
        setItems((cur) => cur.filter((i) => i.thread_id !== id));
    }
  );

  const wasOpen = useRef(false);
  useEffect(() => {
    if (status !== "open") return;
    if (wasOpen.current) void refresh();
    wasOpen.current = true;
  }, [status, refresh]);

  const markSeen = useCallback((threadId: string) => {
    const item = itemsRef.current.find((i) => i.thread_id === threadId);
    if (!item) return;
    if (item.kind === "finished" || item.kind === "failed")
      setItems((cur) => cur.filter((i) => i.thread_id !== threadId));
    AttentionAPI.markSeen(itemScope(item), threadId).catch(() => undefined);
  }, []);

  const snooze = useCallback(
    async (targets: AttentionItem[], minutes = SNOOZE_MINUTES) => {
      const until = new Date(Date.now() + minutes * 60_000).toISOString();
      const ids = new Set(targets.map((t) => t.thread_id));
      setItems((cur) =>
        cur.map((i) =>
          ids.has(i.thread_id) ? { ...i, snoozed_until: until } : i
        )
      );
      const results = await Promise.all(
        targets.map((t) =>
          AttentionAPI.snooze(itemScope(t), t.thread_id, minutes).catch(
            () => null
          )
        )
      );
      return results.find(Boolean) ?? until;
    },
    []
  );

  const hide = useCallback((targets: AttentionItem[]) => {
    const keys = new Set(targets.map(signalKey));
    for (const key of keys) hidden.current.add(key);
    setItems((cur) => cur.filter((i) => !keys.has(signalKey(i))));
    return () => {
      for (const key of keys) hidden.current.delete(key);
    };
  }, []);

  const dismiss = useCallback(
    async (targets: AttentionItem[]) => {
      if (!targets.length) return true;
      const unhide = hide(targets);
      const results = await Promise.all(
        targets.map((t) =>
          AttentionAPI.dismiss(itemScope(t), t.thread_id).then(
            () => true,
            () => false
          )
        )
      );
      if (results.every(Boolean)) return true;
      unhide();
      await refresh();
      return false;
    },
    [hide, refresh]
  );

  const skip = useCallback(
    async (target: AttentionItem) => {
      const unhide = hide([target]);
      try {
        await AttentionAPI.skip(itemScope(target), target.thread_id);
      } catch (error) {
        unhide();
        await refresh();
        throw error;
      }
    },
    [hide, refresh]
  );

  const value = useMemo<AgentAttentionValue>(() => {
    const byTab = new Map<string, AttentionItem[]>();
    for (const item of items) {
      const id = itemTabId(item);
      const list = byTab.get(id);
      if (list) list.push(item);
      else byTab.set(id, [item]);
    }
    return {
      items,
      count: items.filter((i) => countsTowardBadge(i)).length,
      loaded,
      byTab,
      refresh,
      markSeen,
      snooze,
      dismiss,
      skip
    };
  }, [items, loaded, refresh, markSeen, snooze, dismiss, skip]);

  return (
    <Ctx.Provider value={value}>
      {children}
      <AttentionEffects items={items} loaded={loaded} />
    </Ctx.Provider>
  );
};
